import { useState } from "react";
import { Play } from "lucide-react";
import { toast } from "sonner";
import { useFetch } from "../lib/useFetch";
import { api, errMsg, ts, rm } from "../lib/api";
import { useApp } from "../lib/AppContext";
import { Panel, PageHead, Spinner, Btn, Empty } from "../components/common";
import { Switch } from "../components/ui/switch";

const STEPS = ["Identify sessions", "Scan market data", "Analyze instruments", "Detect regime", "Find setups", "Entry / SL / TP", "Size position", "Capital Shield", "Execute paper order", "Monitor positions", "Partial & protective exits", "Reconcile", "Update P&L", "Notify owner", "Report"];

export default function Autonomous() {
  const { refresh } = useApp();
  const { data, reload } = useFetch("/autonomous/status", 15000);
  const [busy, setBusy] = useState(false);
  if (!data) return <Spinner />;
  const toggle = async (v) => { await api.post("/autonomous/toggle", { enabled: v }); reload(); refresh(); };
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/autonomous/run-cycle"); toast.success(`Cycle: ${r.data.outcome}`); reload(); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="autonomous-page">
      <PageHead title="Autonomous AI Trading Manager" sub={`Continuously running orchestrator (every ${data.cycle_seconds}s). It may — and usually will — decide NO TRADE. Profit targets never force entries.`}
        right={<div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-700"><Switch data-testid="autonomous-toggle" checked={data.enabled} onCheckedChange={toggle} />Autonomous {data.enabled ? "ON" : "OFF"}</label>
          <Btn onClick={run} disabled={busy} data-testid="run-cycle-btn"><Play className="h-3.5 w-3.5" />{busy ? "Running…" : "Run cycle now"}</Btn>
        </div>} />
      <div className="grid gap-3 lg:grid-cols-3">
        <Panel title="Orchestrator health" testid="orchestrator-health">
          <div className="mono space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Mode</span><span className="text-amber-600">{data.mode.toUpperCase()}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Job status</span><span>{data.job?.status}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Last heartbeat</span><span>{ts(data.job?.last_heartbeat)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Cycles (this process)</span><span>{data.runtime?.cycles}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Emergency halt</span><span className={data.halted ? "text-red-600" : "text-emerald-600"}>{data.halted ? "HALTED" : "clear"}</span></div>
            {data.job?.last_error && <div className="text-red-600">Error: {data.job.last_error}</div>}
          </div>
        </Panel>
        <Panel title="Decision pipeline" className="lg:col-span-2">
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5">
            {STEPS.map((s, i) => <div key={s} className="rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] p-2"><div className="mono text-[9px] text-blue-500">{String(i + 1).padStart(2, "0")}</div><div className="text-[11px] text-slate-700">{s}</div></div>)}
          </div>
        </Panel>
      </div>
      <Panel title="Cycle log" className="mt-3" testid="cycle-log">
        {data.cycles.length === 0 ? <Empty>No cycles yet</Empty> : (
          <table className="tbl w-full"><thead><tr><th>Time</th><th>Trigger</th><th>Outcome</th><th>Markets open</th><th>Equity</th><th>Decisions</th></tr></thead>
            <tbody>{data.cycles.map((c) => (
              <tr key={c.id}><td className="mono">{ts(c.started_at)}</td><td className="text-slate-600">{c.trigger}</td>
                <td className={`mono font-bold ${c.outcome === "TRADE" ? "text-emerald-600" : "text-slate-600"}`}>{c.outcome}</td>
                <td className="mono">{c.open_markets.join(", ") || "—"}</td><td className="mono">{rm(c.equity)}</td>
                <td className="max-w-[520px] whitespace-normal text-[11px] text-slate-600">{c.decisions.join(" · ")}</td></tr>))}
            </tbody></table>
        )}
      </Panel>
    </div>
  );
}
